import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../../api';
import { entityTypeSchema } from '../../schemas';
import { toast } from '../../utils/toast';
import { ModernBadge, ModernButton, ModernCard, ModernInput } from '../ui/modern';
import Modal from '../ui/Modal';
import IconPicker from '../ui/IconPicker';

const CATEGORIES = [
  { value: 'user', label: 'User', icon: '👥' },
  { value: 'device', label: 'Device', icon: '💻' },
  { value: 'infrastructure', label: 'Infrastructure', icon: '🧰' },
  { value: 'service', label: 'Service', icon: '🧩' },
];

const SCOPES = [
  { value: 'branch', label: 'Branch' },
  { value: 'company', label: 'Company' },
];

function normalizeIcon(value) {
  if (!value) return null;
  const v = String(value).trim();
  return v.length ? v : null;
}

export default function EntityTypeManager() {
  const [sectors, setSectors] = useState([]);
  const [sectorId, setSectorId] = useState(null);
  const [category, setCategory] = useState('user');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const formSchema = useMemo(() => entityTypeSchema, []);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sectorId: 0,
      code: '',
      name: '',
      category: 'user',
      scope: 'branch',
      icon: '',
      sortOrder: 0,
      isActive: true,
      allowMultiple: true,
      isRequired: false,
    },
  });

  const watchedCategory = watch('category');

  async function fetchSectors() {
    try {
      const res = await api.get('/admin/sectors');
      const list = res.data || [];
      setSectors(list);
      const def = list.find((s) => s.isDefault) || list[0];
      setSectorId(def?.id ?? null);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load sectors');
    }
  }

  async function fetchEntityTypes(nextSectorId, nextCategory, bustCache = false) {
    if (!nextSectorId) return;
    setLoading(true);
    try {
      const params = {
        sectorId: nextSectorId,
        category: nextCategory,
        includeInactive: true,
      };
      if (bustCache) params._t = Date.now();
      const res = await api.get('/admin/entity-types', { params });
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load entity types');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSectors();
  }, []);

  useEffect(() => {
    if (sectorId) fetchEntityTypes(sectorId, category);
  }, [sectorId, category]);

  function openCreate() {
    if (!sectorId) return;
    setEditing(null);
    reset({
      sectorId,
      code: '',
      name: '',
      category,
      scope: 'branch',
      icon: '',
      sortOrder: rows.length ? Math.max(...rows.map((r) => Number(r.sortOrder) || 0)) + 10 : 10,
      isActive: true,
      allowMultiple: true,
      isRequired: false,
    });
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    reset({
      sectorId: row.sectorId,
      code: row.code || '',
      name: row.name || '',
      category: row.category || 'user',
      scope: row.scope || 'branch',
      icon: row.icon || '',
      sortOrder: Number(row.sortOrder) || 0,
      isActive: !!row.isActive,
      allowMultiple: !!row.allowMultiple,
      isRequired: !!row.isRequired,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      ...values,
      sectorId: Number(values.sectorId),
      sortOrder: Number(values.sortOrder) || 0,
      icon: normalizeIcon(values.icon),
    };
    try {
      if (editing?.id) {
        await api.put(`/admin/entity-types/${editing.id}`, payload);
        toast.success('Entity type updated');
      } else {
        await api.post('/admin/entity-types', payload);
        toast.success('Entity type created');
      }
      setModalOpen(false);
      await fetchEntityTypes(sectorId, category, true);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save entity type');
    }
  }

  async function disableRow(row) {
    if (!window.confirm(`Disable entity type "${row.name}"?\n\nThis will hide it from onboarding but keep existing data.`)) return;
    try {
      await api.delete(`/admin/entity-types/${row.id}`);
      toast.success('Entity type disabled');
      await fetchEntityTypes(sectorId, category, true);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to disable entity type');
    }
  }

  async function deleteRow(row) {
    if (!window.confirm(`⚠️ PERMANENTLY DELETE "${row.name}"?\n\nThis action CANNOT be undone!`)) return;
    if (!window.confirm(`Are you absolutely sure you want to permanently delete "${row.name}"?`)) return;
    try {
      await api.delete(`/admin/entity-types/${row.id}?hard=true`);
      toast.success('Entity type permanently deleted');
      await fetchEntityTypes(sectorId, category, true);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to delete entity type');
    }
  }

  const sectorOptions = sectors.map((s) => ({ value: String(s.id), label: `${s.name}${s.isDefault ? ' (Default)' : ''}` }));
  const categoryMeta = CATEGORIES.find((c) => c.value === category);

  return (
    <ModernCard>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Entity Types</h2>
          <p className="text-gray-600 dark:text-slate-400 text-sm">Manage users, devices, infrastructure, and services</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="min-w-[260px]">
            <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Sector</label>
            <select
              value={sectorId ?? ''}
              onChange={(e) => setSectorId(Number(e.target.value))}
              className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
            >
              {sectorOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-white dark:bg-slate-800">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <ModernButton variant="primary" onClick={openCreate} disabled={!sectorId}>
            + Add Entity Type
          </ModernButton>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 mb-4">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            onClick={() => setCategory(c.value)}
            className={`px-4 py-3 rounded-2xl font-semibold transition-all border flex-shrink-0 ${
              category === c.value
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-500/20 shadow-lg shadow-purple-500/20'
                : 'bg-white dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/10 border-gray-200 dark:border-white/10'
            }`}
          >
            <span className="mr-2">{c.icon}</span>
            {c.label}
          </button>
        ))}
      </div>

      <div className="text-gray-700 dark:text-slate-300 text-sm mb-3">
        Showing: <span className="text-gray-900 dark:text-white font-semibold">{categoryMeta?.label}</span>
      </div>

      {loading ? (
        <div className="text-gray-700 dark:text-slate-300">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-700 dark:text-slate-300">No entity types found for this sector/category.</div>
      ) : (
        <>
          <div className="md:hidden space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-gray-200 dark:border-white/10 rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-gray-900 dark:text-white font-semibold">{r.icon || '—'} {r.name}</div>
                    <div className="text-xs text-gray-600 dark:text-slate-400">{r.code}</div>
                  </div>
                  <ModernBadge variant={r.isActive ? 'success' : 'secondary'}>
                    {r.isActive ? 'Active' : 'Disabled'}
                  </ModernBadge>
                </div>
                <div className="text-sm text-gray-600 dark:text-slate-400">
                  Scope: <span className="text-gray-900 dark:text-white">{r.scope}</span>
                </div>
                <div className="text-sm text-gray-600 dark:text-slate-400">
                  Required: <span className="text-gray-900 dark:text-white">{r.isRequired ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ModernButton variant="glass" onClick={() => openEdit(r)}>
                    Edit
                  </ModernButton>
                  <ModernButton variant="danger" onClick={() => disableRow(r)} disabled={!r.isActive}>
                    Disable
                  </ModernButton>
                  <ModernButton
                    variant="danger"
                    onClick={() => deleteRow(r)}
                    className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500"
                  >
                    Delete
                  </ModernButton>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
            <thead>
              <tr className="text-gray-600 dark:text-slate-400 text-sm">
                <th className="py-2">Icon</th>
                <th className="py-2">Name</th>
                <th className="py-2">Code</th>
                <th className="py-2">Scope</th>
                <th className="py-2">Required</th>
                <th className="py-2">Active</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-200 dark:border-white/10">
                  <td className="py-3 text-gray-900 dark:text-white text-xl">
                    {r.icon || '—'}
                  </td>
                  <td className="py-3 text-gray-900 dark:text-white font-medium">
                    {r.name}
                  </td>
                  <td className="py-3 text-gray-700 dark:text-slate-300">{r.code}</td>
                  <td className="py-3 text-gray-700 dark:text-slate-300">{r.scope}</td>
                  <td className="py-3">
                    <ModernBadge variant={r.isRequired ? 'success' : 'secondary'}>{r.isRequired ? 'Yes' : 'No'}</ModernBadge>
                  </td>
                  <td className="py-3">
                    <ModernBadge variant={r.isActive ? 'success' : 'secondary'}>{r.isActive ? 'Yes' : 'No'}</ModernBadge>
                  </td>
                  <td className="py-3 text-right space-x-2">
                    <ModernButton variant="glass" onClick={() => openEdit(r)}>
                      Edit
                    </ModernButton>
                    <ModernButton variant="danger" onClick={() => disableRow(r)} disabled={!r.isActive}>
                      Disable
                    </ModernButton>
                    <ModernButton
                      variant="danger"
                      onClick={() => deleteRow(r)}
                      className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500"
                    >
                      Delete
                    </ModernButton>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Entity Type' : 'Add Entity Type'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('sectorId', { valueAsNumber: true })} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModernInput
              label="Name"
              placeholder="What users see"
              error={errors.name?.message}
              {...register('name')}
            />
            <ModernInput
              label="Code"
              placeholder="stable-key (lowercase)"
              error={errors.code?.message}
              {...register('code')}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
              <select
                value={watchedCategory || 'user'}
                {...register('category')}
                className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} className="bg-white dark:bg-slate-800">
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Scope</label>
              <select
                {...register('scope')}
                className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value} className="bg-white dark:bg-slate-800">
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon (optional)
              </label>
              <Controller
                name="icon"
                control={control}
                render={({ field }) => (
                  <IconPicker value={field.value} onChange={field.onChange} />
                )}
              />
              {errors.icon?.message && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.icon.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Sort Order
              </label>
              <Controller
                name="sortOrder"
                control={control}
                render={({ field }) => (
                  <input
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                    onBlur={field.onBlur}
                    className="w-full px-4 py-2.5 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                )}
              />
              {errors.sortOrder?.message && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sortOrder.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
              <input type="checkbox" {...register('isActive')} className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-white/10 rounded focus:ring-blue-500" />
              Active
            </label>
            <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
              <input type="checkbox" {...register('allowMultiple')} className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-white/10 rounded focus:ring-blue-500" />
              Allow Multiple
            </label>
            <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
              <input type="checkbox" {...register('isRequired')} className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-white/10 rounded focus:ring-blue-500" />
              Required
            </label>
          </div>

          <div className="text-xs text-gray-600 dark:text-slate-400 font-medium">
            Category: <span className="text-gray-900 dark:text-slate-200 font-semibold">{CATEGORIES.find((c) => c.value === watchedCategory)?.label}</span> • Sector ID: <span className="text-gray-900 dark:text-slate-200 font-semibold">{sectorId}</span>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <ModernButton variant="glass" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </ModernButton>
            <ModernButton variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </ModernButton>
          </div>
        </form>
      </Modal>
    </ModernCard>
  );
}


